import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
} from "date-fns";
import { computed, type ComputedRef, type Ref } from "vue";

import type { CellPath, Controller, FullNotice } from "@ctrl/controller-rpc";

import { formatRelativeDate } from "@/components/dates";
import { graphRecordToProperties } from "@/components/graph/component";
import { getObjectTitle } from "@/components/graph/title";
import type { BaseLogger } from "@/components/inject";
import { getWorkspace, type UseProfile } from "@/composables/useProfile";
import type { Router } from "@/types";
import {
  internalPathFromRoutineHref,
  internalPathFromRoutineId,
  referenceIdFromString,
} from "@/utils/hyperlink";
import {
  navigateToInternalPathOrObject,
  navigateToObject,
} from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";
import { isRecord } from "@/utils/unknown";

// Extract specific notice types from the FullNotice union
export type MentionNotice = Extract<FullNotice, { event: "mention" }>;
export type SetRelationNotice = Extract<FullNotice, { event: "set_relation" }>;
type NoticeBase = Pick<
  FullNotice,
  | "workspace_id"
  | "created"
  | "id"
  | "location"
  | "message"
  | "recipient"
  | "archived"
  | "viewed"
>;
type CurrentIntegrationErrorNotice = NoticeBase & {
  event: "error";
  error: {
    source: "integration_app";
    origin: { type: "object_push"; object: string };
    error:
      | "create_record_failed"
      | "update_record_failed"
      | "delete_record_failed";
  };
};
type CurrentGoogleErrorNotice = NoticeBase & {
  event: "error";
  error: {
    source: "google";
    origin: {
      account: string;
      peopleSource: "connections" | "directory" | "other" | "self";
    };
    error: string;
  };
};
export type IntegrationErrorNotice = CurrentIntegrationErrorNotice;
export type GoogleErrorNotice = CurrentGoogleErrorNotice;
export type AutomationAskUserNotice = Extract<
  FullNotice,
  { event: "automation_ask_user" }
>;
// GenericNotice is everything that's not one of the specific event types (i.e., notices without an event field)
export type GenericNotice = Exclude<
  FullNotice,
  | MentionNotice
  | SetRelationNotice
  | IntegrationErrorNotice
  | GoogleErrorNotice
  | AutomationAskUserNotice
>;

export function isIntegrationErrorNotice(
  notice: FullNotice
): notice is IntegrationErrorNotice {
  if (!("event" in notice) || notice.event !== "error") {
    return false;
  }

  const error: unknown = notice.error;
  if (!isRecord(error)) {
    return false;
  }

  if (error.source !== "integration_app" || !isRecord(error.origin)) {
    return false;
  }

  return (
    error.origin.type === "object_push" &&
    typeof error.origin.object === "string" &&
    (error.error === "create_record_failed" ||
      error.error === "update_record_failed" ||
      error.error === "delete_record_failed")
  );
}

export function integrationErrorCode(notice: IntegrationErrorNotice): string {
  return notice.error.error;
}

export function integrationErrorObjectId(
  notice: IntegrationErrorNotice
): string {
  return notice.error.origin.object;
}

export function isGoogleErrorNotice(
  notice: FullNotice
): notice is GoogleErrorNotice {
  if (!("event" in notice) || notice.event !== "error") {
    return false;
  }

  const error: unknown = notice.error;
  if (
    !isRecord(error) ||
    error.source !== "google" ||
    !isRecord(error.origin)
  ) {
    return false;
  }

  return (
    typeof error.error === "string" &&
    typeof error.origin.account === "string" &&
    (error.origin.peopleSource === "connections" ||
      error.origin.peopleSource === "directory" ||
      error.origin.peopleSource === "other" ||
      error.origin.peopleSource === "self")
  );
}

/**
 * Returns a computed ref for the relative time display
 * e.g., "5min", "2h", "yesterday", "15 Dec, 2025"
 */
export function useNoticeRelativeTime(
  notice: Ref<FullNotice>
): ComputedRef<string> {
  return computed(() => {
    const created = notice.value.created;
    const now = new Date();
    const minutes = Math.max(0, differenceInMinutes(now, created));
    if (minutes === 0) {
      return "now";
    }
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.max(0, differenceInHours(now, created));
    if (hours < 24) {
      return `${hours}h`;
    }
    const days = Math.max(0, differenceInDays(now, created));
    // Use "yesterday"/"last Thursday"/etc when close enough.
    if (days <= 5) {
      return formatRelativeDate(created, "short");
    }
    // Otherwise show an absolute date like "15 Dec, 2025".
    return format(created, "d MMM, yyyy");
  });
}

/**
 * Fetches the object title from the notice's origin object (non-reactive)
 * Returns null if unable to fetch, allowing caller to adjust message
 */
export async function getNoticeObjectTitle(
  originObjectId: string,
  controller: Controller
): Promise<string | null> {
  return getObjectTitle(controller, originObjectId);
}

/** Returns the affected object's database display name when its row is gone. */
export async function getNoticeObjectTableTitle(
  originObjectId: string,
  controller: Controller
): Promise<string | null> {
  const database = (
    await controller.graph.database.ofObject(originObjectId)
  ).release();
  return database?.data_name ?? null;
}

/**
 * The name of the user who triggered the notice (non-reactive).
 */
export function getTriggeredName(
  notice: MentionNotice | SetRelationNotice,
  profile: UseProfile
): string {
  const workspace = getWorkspace(profile, notice.workspace_id)?.workspace;
  return workspace?.members[notice.triggered]?.name || "Someone";
}

/**
 * Open a notice location inside the app when it is a Routine id or an
 * in-app URL. Raw `object:…` ids are valid URIs, so handing them to the OS
 * produces macOS's "no application set to open the URL" dialog instead of
 * the in-app Not found screen for a deleted object.
 */
export async function openNoticeLocation(
  controller: Controller,
  router: Router,
  baseLogger: BaseLogger,
  location: string
): Promise<void> {
  const logger = baseLogger.forModule("notice");
  try {
    const reference = referenceIdFromString(location);
    if (reference) {
      await navigateToObject(router, controller, reference);
      return;
    }

    const idPath = internalPathFromRoutineId(location);
    if (idPath) {
      await navigateToInternalPathOrObject(controller, router, idPath);
      return;
    }

    const internalPath = internalPathFromRoutineHref(location);
    if (internalPath) {
      await navigateToInternalPathOrObject(controller, router, internalPath);
      return;
    }

    await openExternalUrl(location, controller, true, router);
  } catch (e) {
    logger.error("Failed to open notice location", e);
  }
}

/**
 * Navigates to the origin object of a notice. The object route itself
 * renders Not found when the row no longer exists.
 */
export async function openNoticeObject(
  controller: Controller,
  router: Router,
  baseLogger: BaseLogger,
  originObjectId: string | undefined
): Promise<void> {
  const logger = baseLogger.forModule("notice");
  logger.info("openNoticeObject()", { originObjectId });

  if (!originObjectId) return;
  try {
    await navigateToObject(router, controller, {
      kind: "object",
      id: originObjectId,
    });
  } catch (e) {
    logger.error("openNoticeObject failed", e);
  }
}

export async function openNotice(
  controller: Controller,
  router: Router,
  baseLogger: BaseLogger,
  notice: FullNotice
): Promise<void> {
  const logger = baseLogger.forModule("notice");
  logger.info("openNotice()", notice);

  if (!notice.viewed) {
    try {
      await controller.notice.markViewed(notice);
    } catch (e) {
      logger.error("Unable to mark notice as viewed", e);
      // Best-effort: still open even if marking viewed fails.
    }
  }

  if (isIntegrationErrorNotice(notice) || isGoogleErrorNotice(notice)) {
    await openNoticeLocation(controller, router, baseLogger, notice.location);
    return;
  }

  if (!("event" in notice)) {
    const location = notice.location;
    if (!location) return;
    await openNoticeLocation(controller, router, baseLogger, location);
    return;
  }

  switch (notice.event) {
    case "mention":
    case "set_relation":
      await openNoticeObject(
        controller,
        router,
        baseLogger,
        notice.origin.object
      );
      return;
    case "automation_ask_user":
      router.push({ name: "Automation Run", params: { runId: notice.run_id } });
      return;
    default:
      return;
  }
}

/**
 * Fetches the property name from the notice's path (non-reactive)
 * Returns null if unable to fetch, allowing caller to adjust message
 */
export async function getPropertyNameFromPath(
  objectId: string,
  path: CellPath,
  controller: Controller
): Promise<string | null> {
  if (path.length === 0) {
    return null;
  }

  const columnId = path[0];
  if (typeof columnId !== "string") {
    return null;
  }

  try {
    const { workspace_id, table_id } =
      await controller.graph.object.id.parse(objectId);
    if (!workspace_id || !table_id) {
      return null;
    }

    const database = (
      await controller.graph.database.get(workspace_id, table_id)
    ).release();
    if (!database) {
      return null;
    }

    const properties = graphRecordToProperties(database.type);
    const property = properties.find((p) => p.id === columnId);
    return property?.name || null;
  } catch {
    return null;
  }
}
